import {
  Box,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
} from "@mui/material";
import { Help } from "@mui/icons-material";
import { ApiDomainHoster, ApiDomainStatus } from "@incendium/api";
import GlassCard from "Components/GlassCard/GlassCard";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import { useEffect, useMemo, useState } from "react";
import { useSelectedProject } from "Hooks";
import { useSubdomains } from "Hooks/useSubdomains";
import { metaIAIMapping } from "features/utms/components/MetaAdsTrackingTemplate";
import MetaAdsTrackingSetup from "features/utms/components/MetaAdsTrackingSetup";

function SmartLinkUrlCard() {
  const { selectedProject } = useSelectedProject();
  const { subdomains } = useSubdomains();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [selectedUrlSource, setSelectedUrlSource] = useState("default");
  const [hasChosenUrlSource, setHasChosenUrlSource] = useState(false);

  const smartLinkUrl = useMemo(() => {
    if (!selectedProject?.key) return "";
    return `https://smartlinks.incendium.ai/${selectedProject.key}`;
  }, [selectedProject?.key]);

  const smartLinkUrlOptions = useMemo(() => {
    if (!selectedProject?.key) {
      return [];
    }

    const options = [
      {
        id: "default",
        label: "Incendium Smart Links domain",
        url: smartLinkUrl,
      },
    ];

    const customDomainOptions = (subdomains || [])
      .filter(
        (sd) =>
          sd.hoster === ApiDomainHoster.DOMAIN_HOSTER_CLOUDFLARE &&
          sd.status === ApiDomainStatus.DOMAIN_STATUS_READY &&
          !!sd.subdomain &&
          !!sd.domain,
      )
      .map((sd) => ({
        id: String(sd.id),
        label: sd.cnameName,
        url: `${sd.url}/sm/${selectedProject.key}`,
      }));

    return [...options, ...customDomainOptions];
  }, [selectedProject?.key, smartLinkUrl, subdomains]);

  const selectedSmartLinkUrl = useMemo(() => {
    return (
      smartLinkUrlOptions.find((option) => option.id === selectedUrlSource)
        ?.url ||
      smartLinkUrlOptions[0]?.url ||
      ""
    );
  }, [selectedUrlSource, smartLinkUrlOptions]);

  useEffect(() => {
    setSelectedUrlSource("default");
    setHasChosenUrlSource(false);
  }, [selectedProject?.id]);

  useEffect(() => {
    if (hasChosenUrlSource) {
      return;
    }
    const firstSubdomainOption = smartLinkUrlOptions.find(
      (option) => option.id !== "default",
    );
    if (firstSubdomainOption) {
      setSelectedUrlSource(firstSubdomainOption.id);
    }
  }, [smartLinkUrlOptions, hasChosenUrlSource]);

  const urlParams = useMemo(() => {
    return Object.keys(metaIAIMapping)
      .map((k) => `${k}=${metaIAIMapping[k]}`)
      .join("&");
  }, []);

  if (!selectedSmartLinkUrl) {
    return null;
  }

  return (
    <>
      <GlassCard>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1">Smart Link URL</Typography>

            <Typography variant="body2" color="text.secondary" mb={1}>
              Use this URL in your ad settings.
              <br /> You can choose one of your connected subdomains or the
              Incendium Smart Links domain,
              <br /> and Incendium will automatically map to the correct landing
              pages when configured.
            </Typography>
            {smartLinkUrlOptions.length > 1 && (
              <TextField
                size="small"
                select
                sx={{ my: 1, minWidth: 300 }}
                label="URL domain"
                value={selectedUrlSource}
                onChange={(e) => {
                  setHasChosenUrlSource(true);
                  setSelectedUrlSource(e.target.value);
                }}
              >
                {smartLinkUrlOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {" "}
                    {option.label}{" "}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "monospace",
                  bgcolor: "action.hover",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  wordBreak: "break-all",
                }}
              >
                {selectedSmartLinkUrl}
              </Typography>
              <CopyClipboardBtn text={selectedSmartLinkUrl} size="extraSmall">
                copy
              </CopyClipboardBtn>
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">
                URL Parameters (Required)
              </Typography>
              <IconButton
                size="small"
                onClick={() => setHelpDialogOpen(true)}
                sx={{ p: 0.5 }}
              >
                <Help fontSize="small" color="primary" />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Add these URL parameters to your ad's URL parameters field. This
              is required for Incendium to map ads to landing pages.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "monospace",
                  bgcolor: "action.hover",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  wordBreak: "break-all",
                }}
              >
                {urlParams}
              </Typography>
              <CopyClipboardBtn text={urlParams} size="extraSmall">
                copy
              </CopyClipboardBtn>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              If you already have URL parameters in this field, append these to
              the end: <code>&amp;{urlParams}</code>
            </Typography>
          </Box>
        </Stack>
      </GlassCard>

      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <MetaAdsTrackingSetup onFinish={() => setHelpDialogOpen(false)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SmartLinkUrlCard;
